import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  inject,
  ViewChild,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { MenuModule } from 'primeng/menu';
import { Menu } from 'primeng/menu';
import { MessageService } from 'primeng/api';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { Subject, takeUntil, filter } from 'rxjs';
import { signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { TableColumn } from '@shared/components/data-table/data-table.component';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import { SearchFormComponent } from '@shared/components/search-form/search-form.component';
import { StatusFilterComponent } from '@shared/components/status-filter/status-filter.component';
import {
  ConfirmationDialogComponent,
  ConfirmationButton,
} from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import { DateFormatterService } from '@shared/services/date-formatter.service';
import {
  PurchaseOrderService,
  SearchFormData,
} from './services/purchase-order.service';
import { PermissionService } from '@app/core/services/permission.service';
import {
  PurchaseOrderItem,
  PURCHASE_ORDER_SEARCH_CONFIG,
  PURCHASE_ORDER_COLUMNS,
  PURCHASE_ORDER_DEFAULT_COLUMNS_ORDER,
  PURCHASE_ORDER_FLEXIBLE_COLUMNS_ORDER,
  PURCHASE_ORDER_ACTION_COLUMN,
  PURCHASE_ORDER_BUTTON_CONFIG,
  getStatusSeverity,
  getStatusDisplayText,
  getStatusIcon,
  getStatusStyle,
  formatCurrency,
} from './purchase-order.config';
import { openAdvancedTableColumnSelector } from '@shared/utils/advanced-table-column-selector.util';
import { mergeOneBasedPageFromPrimeEvent } from '@shared/utils/prime-paginator-one-based.util';

@Component({
  selector: 'app-purchase-order',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    ToastModule,
    MenuModule,
    AdvancedDataTableComponent,
    SearchFormComponent,
    StatusFilterComponent,
    ConfirmationDialogComponent,
    AppButtonComponent,
  ],
  providers: [MessageService],
  templateUrl: './purchase-order.component.html',
  styleUrl: './purchase-order.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PurchaseOrderComponent implements OnInit, OnDestroy {
  // Configuration
  searchFormConfig = PURCHASE_ORDER_SEARCH_CONFIG;

  // Check if user has any action permissions (READ, UPDATE, DELETE, APPROVE, or vendor confirm)
  hasAnyActionPermission = computed(
    () =>
      this.canRead() ||
      this.canUpdate() ||
      this.canDelete() ||
      this.canApprove() ||
      this.canConfirmVendor(),
  );

  // Advanced table column configuration
  defaultColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      PURCHASE_ORDER_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];
    for (const key of PURCHASE_ORDER_DEFAULT_COLUMNS_ORDER) {
      const col = fromConfig(key);
      if (col) cols.push(col);
    }

    // Conditionally add actions column at the end if permitted
    if (this.hasAnyActionPermission()) {
      const actionsCol = fromConfig(PURCHASE_ORDER_ACTION_COLUMN.ACTIONS);
      if (actionsCol) cols.push(actionsCol);
    }

    return cols;
  });

  flexibleColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      PURCHASE_ORDER_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];

    for (const prop of PURCHASE_ORDER_FLEXIBLE_COLUMNS_ORDER) {
      const col = fromConfig(prop);
      if (col) {
        cols.push(col);
      }
    }

    return cols;
  });

  // Primary button configuration base
  private readonly primaryButtonBase = PURCHASE_ORDER_BUTTON_CONFIG;

  // Primary button configuration - computed with permissions
  primaryButton = computed(() => ({
    ...this.primaryButtonBase,
    show: this.canCreate(),
  }));

  // Confirmation dialog properties
  showConfirmationDialog = false;
  selectedPurchaseOrder: PurchaseOrderItem | null = null;
  dialogTitle = '';
  dialogMessage = '';
  dialogButtons: ConfirmationButton[] = [];
  currentAction: 'delete' | null = null;

  // Data signals
  purchaseOrders = signal<PurchaseOrderItem[]>([]);
  loading = signal(false);
  totalRecords = signal(0);

  // Menu properties
  @ViewChild('actionMenu') actionMenu!: Menu;
  currentMenuModel = signal<any[]>([]);
  selectedRowData: PurchaseOrderItem | null = null;

  // Search parameters (using 1-based pagination like brand component)
  searchParams = signal({
    purchaseRequest: undefined as number | undefined,
    series: '',
    vendorCode: '',
    status: undefined as string | undefined,
    page: 1, // Use 1-based pagination
    size: 20,
  });

  // Private properties
  private readonly destroy$ = new Subject<void>();
  private readonly translateService = inject(TranslateService);
  private readonly dateFormatterService = inject(DateFormatterService);
  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );

  // Permission flags - computed from PermissionService
  // Using specific submenu 'purchase_order' for procurement permissions
  canRead = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('procurement.purchase_order.view');
  });
  canCreate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('procurement.purchase_order.create');
  });
  canUpdate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('procurement.purchase_order.edit');
  });
  canDelete = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('procurement.purchase_order.delete');
  });
  canApprove = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('procurement.purchase_order.approve');
  });
  /** Staff (approve) or vendor portal (confirm_vendor) — menus assigned by Admin. */
  canConfirmVendor = computed(() => {
    this.permissionCodes();
    return (
      this.permissionService.canDoAction('procurement.purchase_order.approve') ||
      this.permissionService.canDoAction(
        'procurement.purchase_order.confirm_vendor',
      )
    );
  });

  constructor(
    private readonly router: Router,
    private readonly purchaseOrderService: PurchaseOrderService,
    private readonly messageService: MessageService,
  ) {}

  ngOnInit(): void {
    // Columns are now computed based on permissions, no need to set them here

    this.loadPurchaseOrders();
    this.setupRouterListener();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Load Purchase Orders
  private loadPurchaseOrders(): void {
    this.loading.set(true);
    const params = {
      ...this.searchParams(),
      page: this.searchParams().page - 1, // Convert 1-based to 0-based for API
    };
    this.purchaseOrderService.getPurchaseOrders(params).subscribe({
      next: (response) => {
        this.purchaseOrders.set(response.data as PurchaseOrderItem[]);
        this.totalRecords.set(response.totalRecords);
        this.loading.set(false);
      },
      error: (error) => {
        this.loading.set(false);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant(
            'purchase_order.messages.error.failed_to_load_orders',
          ),
        });
      },
    });
  }

  private setupRouterListener(): void {
    // Listen for navigation events to refresh data when returning to this page
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe((event: NavigationEnd) => {
        // Check if we're navigating to the purchase order list page
        if (
          event.url.includes('/procurement/purchase-order') &&
          !event.url.includes('/add') &&
          !event.url.includes('/edit') &&
          !event.url.includes('/view')
        ) {
          // Refresh data when returning to list page
          this.loadPurchaseOrders();
        }
      });
  }

  onSearch(searchData: SearchFormData): void {
    this.searchParams.set({ ...this.searchParams(), ...searchData, page: 1 });
    this.loadPurchaseOrders();
  }

  onReset(): void {
    this.searchParams.set({
      purchaseRequest: undefined,
      series: '',
      vendorCode: '',
      status: undefined,
      page: 1,
      size: 20,
    });
    this.loadPurchaseOrders();
  }

  onPageChange(event: any): void {
    this.searchParams.set(
      mergeOneBasedPageFromPrimeEvent(this.searchParams(), event),
    );
    this.loadPurchaseOrders();
  }

  onAdd(): void {
    if (!this.canCreate()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Permission Denied',
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.router.navigate(['/procurement/purchase-order/add']);
  }

  onView(purchaseOrder: PurchaseOrderItem): void {
    if (!this.canRead()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Permission Denied',
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.router.navigate([
      '/procurement/purchase-order/view',
      purchaseOrder.id,
    ]);
  }

  onEdit(purchaseOrder: PurchaseOrderItem): void {
    if (!this.canUpdate()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Permission Denied',
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.router.navigate([
      '/procurement/purchase-order/edit',
      purchaseOrder.id,
    ]);
  }

  onViewPurchaseRequest(purchaseRequestId: string | number): void {
    this.router.navigate([
      '/procurement/purchase-request/view',
      purchaseRequestId,
    ]);
  }

  onDelete(purchaseOrder: PurchaseOrderItem): void {
    if (!this.canDelete()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Permission Denied',
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.selectedPurchaseOrder = purchaseOrder;
    this.dialogTitle = this.translateService.instant(
      'common.confirmation.title',
    );
    this.dialogMessage = this.translateService.instant(
      'purchase_order.messages.confirmation.delete_order',
      { id: purchaseOrder.id },
    );
    this.currentAction = 'delete';
    this.dialogButtons = [
      {
        label: this.translateService.instant('common.actions.cancel'),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant('common.actions.delete'),
        action: 'confirm',
        severity: 'danger',
      },
    ];
    this.showConfirmationDialog = true;
  }

  // Confirmation dialog methods (old implementation removed)

  private executeConfirmedAction(): void {
    if (!this.selectedPurchaseOrder) return;

    if (this.currentAction === 'delete') {
      this.confirmDelete();
    }
  }

  private confirmDelete(): void {
    if (this.selectedPurchaseOrder) {
      this.purchaseOrderService
        .deletePurchaseOrder(this.selectedPurchaseOrder.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (message) => {
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('common.messages.success'),
              detail: this.translateService.instant(
                'purchase_order.messages.success.order_deleted',
              ),
            });
            this.loadPurchaseOrders(); // Refresh data
          },
          error: (error) => {
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('common.messages.error'),
              detail: this.translateService.instant(
                'purchase_order.messages.error.failed_to_delete_order',
              ),
            });
          },
        });
    }
    this.closeConfirmationDialog();
  }

  private closeConfirmationDialog(): void {
    this.showConfirmationDialog = false;
    this.selectedPurchaseOrder = null;
    this.currentAction = null;
    this.dialogTitle = '';
    this.dialogMessage = '';
    this.dialogButtons = [];
  }

  // Status methods
  getStatusSeverity(
    status: string,
  ): 'success' | 'warning' | 'danger' | 'secondary' {
    return getStatusSeverity(status);
  }

  getStatusDisplayText(status: string): string {
    return getStatusDisplayText(status, this.translateService);
  }

  getStatusIcon(status: string): string {
    return getStatusIcon(status);
  }

  getStatusStyle(status: string): any {
    return getStatusStyle(status);
  }

  // Currency formatting
  formatCurrency(amount: number): string {
    return formatCurrency(amount);
  }

  // Date formatting using DateFormatterService
  formatDate(dateInput: any): string {
    if (!dateInput) return '';
    return this.dateFormatterService.formatDateTime(dateInput);
  }

  // Format delivery date to show only date (DD/MM/YYYY)
  formatDeliveryDate(dateInput: any): string {
    if (!dateInput) return '';
    return this.dateFormatterService.formatDate(dateInput);
  }

  // Get available actions for a purchase order based on its status
  getPurchaseOrderActions(purchaseOrder: PurchaseOrderItem): string[] {
    /**
     * Workflow:
     * PENDING:    Approve, Reject, Cancel, Edit
     * APPROVED:   Send, Cancel
     * SENT:       Confirm Supplier, Cancel, Edit
     * VENDOR_CONFIRMED: Deliver, Cancel
     * DELIVERED:  Done
     * DONE:       Close
     * CANCELLED/REJECTED/CLOSED: none
     */
    const status = purchaseOrder.status;
    const actions: string[] = [];
    switch (status) {
      case 'PENDING':
        actions.push('approve', 'reject', 'cancel', 'edit');
        break;
      case 'APPROVED':
        actions.push('send', 'cancel');
        break;
      case 'SENT':
        actions.push('confirm_vendor', 'cancel', 'edit');
        break;
      case 'VENDOR_CONFIRMED':
        actions.push('deliver', 'cancel');
        break;
      case 'DELIVERED':
        actions.push('done');
        break;
      case 'DONE':
        actions.push('close');
        break;
      case 'CANCELLED':
      case 'REJECTED':
      case 'CLOSED':
        // no actions (maybe view already outside this)
        break;
      default:
        break;
    }
    return actions;
  }

  // Check if purchase order has any enabled actions (excluding view)
  hasEnabledPurchaseOrderActions(purchaseOrder: PurchaseOrderItem): boolean {
    const actions = this.getPurchaseOrderActions(purchaseOrder);
    return actions.some((action) => {
      if (action === 'view') return false; // View is handled separately
      if (action === 'edit') return this.canUpdate();
      if (action === 'delete' || action === 'cancel') return this.canDelete();
      if (action === 'confirm_vendor') {
        return this.canConfirmVendor();
      }
      if (
        [
          'approve',
          'reject',
          'send',
          'deliver',
          'done',
          'close',
        ].includes(action)
      ) {
        return this.canApprove();
      }
      return true;
    });
  }

  // Toggle menu for actions
  toggleMenu(event: Event, rowData: PurchaseOrderItem): void {
    this.selectedRowData = rowData;
    const actions = this.getPurchaseOrderActions(rowData);

    // Create menu items based on available actions with permission checks
    // Filter out actions that user doesn't have permission for
    const menuItems = actions
      .filter((action) => {
        // Check permissions for each action - only include if user has permission
        if (action === 'view') {
          return this.canRead();
        } else if (action === 'edit') {
          return this.canUpdate();
        } else if (action === 'delete' || action === 'cancel') {
          return this.canDelete();
        } else if (action === 'confirm_vendor') {
          return this.canConfirmVendor();
        } else if (
          [
            'approve',
            'reject',
            'send',
            'deliver',
            'done',
            'close',
          ].includes(action)
        ) {
          return this.canApprove();
        }
        return true;
      })
      .map((action) => ({
        label: this.translateService.instant(`common.actions.${action}`),
        command: () => this.onActionClick(action, rowData),
      }));

    this.currentMenuModel.set(menuItems);

    // Show menu at cursor position
    this.actionMenu.toggle(event);
  }

  // Handle action clicks
  onActionClick(action: string, rowData: PurchaseOrderItem): void {
    switch (action) {
      case 'view':
        this.onView(rowData);
        break;
      case 'edit':
        this.onEdit(rowData);
        break;
      case 'approve':
        this.onApprove(rowData);
        break;
      case 'send':
        this.onSend(rowData);
        break;
      case 'confirm_vendor':
        this.onConfirmSupplier(rowData);
        break;
      case 'deliver':
        this.onDeliver(rowData);
        break;
      case 'done':
        this.onDone(rowData);
        break;
      case 'close':
        this.onClose(rowData);
        break;
      case 'reject':
        this.onReject(rowData);
        break;
      case 'cancel':
        this.onCancel(rowData);
        break;
      case 'delete':
        this.deletePurchaseOrderWithConfirmation(rowData);
        break;
    }
  }

  // Status-specific action methods
  onApprove(purchaseOrder: PurchaseOrderItem): void {
    if (!this.canApprove()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Permission Denied',
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.updatePurchaseOrderStatus(purchaseOrder, 'APPROVED');
  }

  onSend(purchaseOrder: PurchaseOrderItem): void {
    if (!this.canApprove()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Permission Denied',
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    const purchaseOrderId = Number.parseInt(purchaseOrder.id, 10);
    this.purchaseOrderService.sendPurchaseOrderToVendor(purchaseOrderId).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('common.messages.success'),
          detail: this.translateService.instant(
            'purchase_order.messages.success.send_to_vendor',
          ),
        });
        this.loadPurchaseOrders();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant(
            'purchase_order.messages.error.send_to_vendor_failed',
          ),
        });
      },
    });
  }

  onConfirmSupplier(purchaseOrder: PurchaseOrderItem): void {
    if (!this.canConfirmVendor()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Permission Denied',
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.router.navigate([
      '/procurement/purchase-order/confirm-vendor',
      purchaseOrder.id,
    ]);
  }

  onDeliver(purchaseOrder: PurchaseOrderItem): void {
    this.updatePurchaseOrderStatus(purchaseOrder, 'DELIVERED');
  }

  onDone(purchaseOrder: PurchaseOrderItem): void {
    if (!this.canApprove()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Permission Denied',
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.updatePurchaseOrderStatus(purchaseOrder, 'DONE');
  }

  onClose(purchaseOrder: PurchaseOrderItem): void {
    if (!this.canApprove()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Permission Denied',
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.updatePurchaseOrderStatus(purchaseOrder, 'CLOSED');
  }

  onReject(purchaseOrder: PurchaseOrderItem): void {
    if (!this.canApprove()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Permission Denied',
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.updatePurchaseOrderStatus(purchaseOrder, 'REJECTED');
  }

  onCancel(purchaseOrder: PurchaseOrderItem): void {
    if (!this.canDelete()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Permission Denied',
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.updatePurchaseOrderStatus(purchaseOrder, 'CANCELLED');
  }

  // Update purchase order status
  private updatePurchaseOrderStatus(
    purchaseOrder: PurchaseOrderItem,
    status:
      | 'PENDING'
      | 'APPROVED'
      | 'REJECTED'
      | 'SENT'
      | 'VENDOR_CONFIRMED'
      | 'DELIVERED'
      | 'DONE'
      | 'CLOSED'
      | 'CANCELLED',
  ): void {
    const purchaseOrderId = Number.parseInt(purchaseOrder.id);

    this.purchaseOrderService
      .updatePurchaseOrderStatus(purchaseOrderId, status)
      .subscribe({
        next: (response) => {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'purchase_order.messages.status_updated',
              { status: this.getStatusDisplayText(status) },
            ),
          });

          // Refresh the purchase orders list
          this.loadPurchaseOrders();
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'purchase_order.messages.status_update_failed',
            ),
          });
        },
      });
  }

  // Delete purchase order with confirmation
  private deletePurchaseOrderWithConfirmation(
    purchaseOrder: PurchaseOrderItem,
  ): void {
    if (!this.canDelete()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Permission Denied',
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.selectedPurchaseOrder = purchaseOrder;
    this.dialogTitle = this.translateService.instant(
      'purchase_order.messages.delete.title',
    );
    this.dialogMessage = this.translateService.instant(
      'purchase_order.messages.delete.message',
      { series: purchaseOrder.series },
    );
    this.dialogButtons = [
      {
        label: this.translateService.instant('common.actions.cancel'),
        severity: 'secondary',
        action: 'cancel',
      },
      {
        label: this.translateService.instant('common.actions.delete'),
        severity: 'danger',
        action: 'delete',
      },
    ];
    this.currentAction = 'delete';
    this.showConfirmationDialog = true;
  }

  // Handle confirmation dialog button clicks
  onConfirmatiButtonClick(action: string): void {
    if (action === 'delete' && this.selectedPurchaseOrder) {
      this.executeDeletePurchaseOrder(this.selectedPurchaseOrder);
    }
    this.showConfirmationDialog = false;
    this.selectedPurchaseOrder = null;
    this.currentAction = null;
  }

  // Execute delete purchase order
  private executeDeletePurchaseOrder(purchaseOrder: PurchaseOrderItem): void {
    const purchaseOrderId = Number.parseInt(purchaseOrder.id);
    const itemIds =
      purchaseOrder.items?.map((item) => Number.parseInt(item.id)) || [];

    this.purchaseOrderService
      .deletePurchaseOrderItem(purchaseOrderId, itemIds)
      .subscribe({
        next: (response) => {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'purchase_order.messages.deleted_successfully',
            ),
          });

          // Refresh the purchase orders list
          this.loadPurchaseOrders();
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'purchase_order.messages.delete_failed',
            ),
          });
        },
      });
  }

  // Reference to advanced table component
  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;

  onColumnConfigClick(event: Event): void {
    openAdvancedTableColumnSelector(this.advancedTable, event);
  }
}
